// Dependency imports
import { Stack, IconButton } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { Delete, Edit, Help } from '@mui/icons-material';
import { useEffect, useState } from 'react';

// Local imports
import DataGridComponent from 'components/DataGrid';
import { Button, Modal, Typography } from 'components';
import { useListCameraDetails } from 'api/sdk';
import { useDeleteCameraData } from 'api/sdk';
import { useAddCameraData } from 'api/sdk';
import { useSnack } from 'plugins/snack';
import Swal from 'sweetalert2';
import AddCamera from './addCamera';
import EditCamera from './editCamera';

const Cameras = ({ url, setUrl, data, refetch, setName, name }: any) => {
  const [row, setRows] = useState<any>([]);
  const [openEditModel, setOpenEditModel] = useState<boolean>(false);
  const [cameraId, setCameraId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: deleteCamera } = useDeleteCameraData();
  const { mutateAsync: addCameras, isLoading: loadingAdd } = useAddCameraData();
  const snack = useSnack();

  useEffect(() => {
    setRows(data?.data);
  }, [data]);

  const ActionToolTip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black
    }
  }));

  const handleDeleteClick = async (cameraId: string) => {
    Swal.fire({
      title: 'Delete Camera',
      text: 'Are you sure you want to delete this camera?',
      allowOutsideClick: false,
      confirmButtonText: 'Yes, delete it!',
      showCancelButton: true,
      confirmButtonColor: '#E33030',
      cancelButtonColor: '#102633',
      cancelButtonText: 'No, cancel!',
      focusCancel: true,
      animation: true,
      customClass: {
        title: 'swal2-title',
        container: 'swal2-html-container',
        actions: 'swal2-actions'
      }
    }).then(async result => {
      if (result.isConfirmed) {
        await deleteCamera({
          id: cameraId
        });
        snack({
          message: 'Camera deleted successfully',
          severity: 'success'
        });
        refetch();
      }
    });
  };

  const CameraAction = ({
    cameraId,
    rowValue
  }: {
    cameraId: string;
    rowValue: any;
  }) => {
    return (
      <Stack direction="row" alignItems="center">
        <div>
          <ActionToolTip title={'Edit'}>
            <IconButton
              onClick={() => {
                setName(rowValue.name);
                setUrl(rowValue.url);
                setCameraId(rowValue.id);
                setOpenEditModel(true);
              }}
              disabled={false}
            >
              <Edit className="w-[1.4vw]" />
            </IconButton>
          </ActionToolTip>
        </div>
        <div>
          <ActionToolTip title={'Delete'}>
            <IconButton
              onClick={() => {
                handleDeleteClick(cameraId);
              }}
            >
              <Delete className="w-[1.4vw]" />
            </IconButton>
          </ActionToolTip>
        </div>
      </Stack>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Camera Name',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: (params: any) => {
        return (
          <h2
            style={{
              fontSize: '1vw',
              fontWeight: 400,
              margin: 0,
              padding: 0
            }}
          >
            {params.value?.toString()}
          </h2>
        );
      }
    },
    {
      field: 'url',
      headerName: 'URL',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: (params: any) => {
        return (
          <h2
            style={{
              fontSize: '1vw',
              fontWeight: 400,
              margin: 0,
              padding: 0
            }}
          >
            {params.value?.toString()}
          </h2>
        );
      }
    },
    {
      field: 'action',
      headerName: 'Action',
      headerClassName: 'super-app-theme--header',
      filterable: false,
      disableReorder: true,
      disableColumnMenu: true,
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        return <CameraAction cameraId={params.row.id} rowValue={params.row} />;
      }
    }
  ];

  return (
    <div className="w-full">
      <div className="w-full flex flex-col justify-center gap-4">
        <DataGridComponent
          rows={row}
          columns={columns}
          checkboxSelection={false}
          height={'70vh'}
          disableRowSelectionOnClick={true}
        />
      </div>

      <Modal
        open={openEditModel}
        onClose={() => {
          setOpenEditModel(false);
        }}
        size="md"
      >
        <EditCamera
          url={url}
          name={name}
          setName={setName}
          setUrl={setUrl}
          cameraId={cameraId}
          setOpenEditModel={setOpenEditModel}
          refetch={refetch}
        />
      </Modal>
    </div>
  );
};

export default Cameras;
